import { useMemo, useState } from 'react';
import { Link } from '@/src/i18n/navigation';
import { useTranslations } from 'next-intl';
import { Button, Divider, List } from '@zillow/constellation-10';
import { css } from '@/styled-system/css';
import AppleBadge from '@/src/components/ui/icons/AppleBadge';
import AndroidBadge from '@/src/components/ui/icons/AndroidBadge';
import { hamburgerAppBadges } from '@/src/components/ui/Footer/footerLinks';
import { useCurrentArea } from '@/src/lib/hooks/query/useCurrentArea';
import { shouldDisableAppRouterPrefetch } from '@/src/lib/router-compat/prefetch';
import Drawer from '@/src/components/Drawer';
import { FeedbackModal } from './FeedbackModal';

type HamburgerMenuProps = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
};

const HELP_URL = 'https://help.hotpads.com/hc/en-us';

export default function HamburgerMenu({ id, isOpen, onClose }: HamburgerMenuProps) {
  const t = useTranslations('nav');
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const { data: currentArea } = useCurrentArea();
  const marketResourceId = currentArea?.resourceId;

  // Market known (['area','current'] is seeded on SRP/HDP/BDP — deliberately NOT the homepage's
  // IP-resolved ['area','nearMe'] seed, so crawlers' arbitrary IP locations never geo the homepage
  // menu): geo SRP links, with no "All rentals"
  // item because apartments-for-rent is the unfiltered base SRP (HP-8484). Otherwise every
  // property-type *-near-me slug from the BE search-slug registry (GET /web/v3/search/slugs):
  // All rentals first, then by national listing volume (numUnits sampled across 10 large metros,
  // 2026-07).
  const propertyTypeLinks = useMemo(
    () =>
      marketResourceId
        ? [
            { href: `/${marketResourceId}/apartments-for-rent`, label: t('apartments') },
            { href: `/${marketResourceId}/condos-for-rent`, label: t('condos') },
            { href: `/${marketResourceId}/houses-for-rent`, label: t('houses') },
            { href: `/${marketResourceId}/townhomes-for-rent`, label: t('townhomes') },
            { href: `/${marketResourceId}/duplexes-for-rent`, label: t('duplexes') },
            { href: `/${marketResourceId}/rooms-for-rent`, label: t('rooms') },
          ]
        : [
            { href: '/rentals-near-me', label: t('allRentals') },
            { href: '/apartments-near-me', label: t('apartments') },
            { href: '/condos-near-me', label: t('condos') },
            { href: '/houses-near-me', label: t('houses') },
            { href: '/townhomes-near-me', label: t('townhomes') },
            { href: '/duplexes-near-me', label: t('duplexes') },
            { href: '/rooms-for-rent-near-me', label: t('rooms') },
          ],
    [marketResourceId, t],
  );

  const utilityLinks = useMemo(
    () => [
      { href: '/list-your-rental', label: t('listYourRental') },
      { href: '/pay-your-rent', label: t('payYourRent') },
    ],
    [t],
  );

  const handleSendFeedback = () => {
    setIsFeedbackOpen(true);
    onClose();
  };

  return (
    <>
      {/* Crawler-visible copy of the drawer links: the Drawer mounts nothing into the DOM until opened (HP-8501). */}
      <nav hidden>
        <ul>
          {[...propertyTypeLinks, ...utilityLinks].map((item) => (
            <li key={item.href}>
              <Link href={item.href as React.ComponentProps<typeof Link>['href']} prefetch={false}>
                {item.label}
              </Link>
            </li>
          ))}
          <li>
            <a href={HELP_URL}>{t('help')}</a>
          </li>
        </ul>
      </nav>
      <Drawer
        id={id}
        open={isOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            onClose();
          }
        }}
        ariaLabel={t('navigationMenu')}
        closeLabel={t('closeMenu')}
        footer={
          <div className={badgeRow}>
            <a
              href={hamburgerAppBadges.ios.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={css({ display: 'inline-flex' })}
            >
              {/* eslint-disable-next-line hotpads/no-magic-numbers -- design-specific dimension; follow-up tokenization */}
              <AppleBadge className={css({ height: '36px', width: 'auto' })} />
            </a>
            <a
              href={hamburgerAppBadges.android.href}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={css({ display: 'inline-flex' })}
            >
              {/* eslint-disable-next-line hotpads/no-magic-numbers -- design-specific dimension; follow-up tokenization */}
              <AndroidBadge className={css({ height: '36px', width: 'auto' })} />
            </a>
          </div>
        }
      >
        <nav>
          <List.Root appearance="none">
            {propertyTypeLinks.map((item) => (
              <List.Item key={item.href}>
                <NavLinkButton href={item.href} onClose={onClose}>
                  {item.label}
                </NavLinkButton>
              </List.Item>
            ))}
          </List.Root>
          <Divider />
          <List.Root appearance="none">
            {utilityLinks.map((item) => (
              <List.Item key={item.href}>
                <NavLinkButton href={item.href} onClose={onClose}>
                  {item.label}
                </NavLinkButton>
              </List.Item>
            ))}
            <List.Item>
              <NavActionButton onClick={handleSendFeedback}>{t('sendFeedback')}</NavActionButton>
            </List.Item>
            <List.Item>
              <NavExternalLinkButton href={HELP_URL}>{t('help')}</NavExternalLinkButton>
            </List.Item>
          </List.Root>
        </nav>
      </Drawer>
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} />
    </>
  );
}

const menuButtonAlign = css.raw({ justifyContent: 'flex-start' });

const badgeRow = css({
  alignItems: 'center',
  display: 'flex',
  gap: 'tighter',
  inlineSize: '100%',
  justifyContent: 'center',
});

function NavLinkButton({ href, onClose, children }: { href: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <Button asChild tone="neutral" emphasis="bare" fluid css={menuButtonAlign}>
      <Link
        href={href as React.ComponentProps<typeof Link>['href']}
        prefetch={shouldDisableAppRouterPrefetch(href) ? false : undefined}
        onClick={onClose}
      >
        {children}
      </Link>
    </Button>
  );
}

function NavActionButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <Button type="button" tone="neutral" emphasis="bare" fluid css={menuButtonAlign} onClick={onClick}>
      {children}
    </Button>
  );
}

function NavExternalLinkButton({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Button asChild tone="neutral" emphasis="bare" fluid css={menuButtonAlign}>
      <a href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    </Button>
  );
}
